import {
  PercentageIcon,
  useTeamOrgTree,
  EntityKinds,
} from '@internal/backstage-plugin-common-library-react';
import { TeamDescendantsTree } from '@internal/plugin-techinsights-module-entities';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import FolderIcon from '@mui/icons-material/Folder';
import PeopleIcon from '@mui/icons-material/People';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Popover from '@mui/material/Popover';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { DateTime } from 'luxon';
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useAsync } from 'react-use';
import * as XLSX from 'xlsx';

import { Content, Header, Page, Progress } from '@backstage/core-components';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import { makeStyles, useTheme } from '@material-ui/core/styles';

import { useGetCategories } from '../../hooks';
import { useGetCategoriesData } from '../../hooks/useGetCategoriesData';
import { useGetTeams } from '../../hooks/useGetTeams';
import { CategoryData, CategoryPeriodValue } from '../../types';
import { toStandardISO, useScorecardLinkBuilder } from '../../utils';

type FlatRow = {
  id: string;
  teamId: string;
  depth: number;
  isOrgUnit: boolean;
  hasChildren: boolean;
  parentId: string | null;
  scores: Record<string, number | 'N/A'>;
  overall: number | 'N/A';
  ouName: string;
};

type TabView = 'mine' | 'all';
type SortKey = 'overall' | string;
type SortDir = 'asc' | 'desc' | null;

const EXCLUDED_CATEGORIES = new Set(['KTLO']);
const MANAGER_REGEX = /\s*\(MANAGER:[^)]*\)/i;
const stripManager = (name: string) => name.replace(MANAGER_REGEX, '').trim();

const stripOuPrefix = (teamName: string, ouName: string): string => {
  const normalizedOu = ouName.toUpperCase().trim();
  const normalizedTeam = teamName.toUpperCase().trim();
  for (const sep of [' - ', ' – ', ': ', ' / ']) {
    const prefix = normalizedOu + sep;
    if (normalizedTeam.startsWith(prefix))
      return teamName.slice(prefix.length).trim();
  }
  return teamName;
};

type ColGroup = { label: string; cols: string[] };

const COLUMN_GROUPS: ColGroup[] = [
  {
    label: 'Critical',
    cols: [
      'Java SDK Resource',
      'Agent Coverage',
      'Privileged Identity Management',
      'VM Vulnerability Compliance',
      'Public IP',
      'End of Life',
      'Key Rotation',
    ],
  },
  {
    label: 'High',
    cols: [
      'Criticality Set',
      'Subscription Ownership',
      'Resource Group Tag',
      'Repository Ownership',
      'Blackduck Coverage',
      'Veracode Coverage',
    ],
  },
  {
    label: 'Medium',
    cols: [
      'Information Classification Compliance',
      'CI/CD Success Rate',
      'Commit Signature Verification',
      'UPM',
    ],
  },
  {
    label: 'Low',
    cols: ['Velocity', 'Orphaned Issues', 'Investment Category'],
  },
];

const OU_ANNOTATION = 'graph.microsoft.com/organization-unit-name';

const useCurrentUserOu = (): { ouName: string | null; loading: boolean } => {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const { value, loading } = useAsync(async () => {
    const { userEntityRef } = await identityApi.getBackstageIdentity();
    if (!userEntityRef) return null;
    const [, nameWithNs] = userEntityRef.split(':');
    const [namespace, name] = (nameWithNs ?? '').split('/');
    const entity = await catalogApi.getEntityByRef({
      kind: 'User',
      namespace: namespace ?? 'default',
      name,
    });
    return (
      (entity?.metadata?.annotations?.[OU_ANNOTATION] as string | undefined) ??
      null
    );
  }, []);
  return { ouName: value ?? null, loading };
};

const NAME_COL_WIDTH = 320;
const OVERALL_COL_WIDTH = 68;
const GROUP_HEADER_HEIGHT = 26;

const useStyles = makeStyles(theme => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  tabsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tabBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    padding: '4px 10px 5px',
    fontSize: '0.75rem',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    background: 'none',
    border: 'none',
    borderBottom: '2px solid transparent',
    cursor: 'pointer',
    transition: 'color 0.15s, border-color 0.15s',
    marginBottom: -1,
    letterSpacing: '0.01em',
    '&:hover': { color: theme.palette.text.primary },
  },
  tabBtnActive: {
    color: theme.palette.primary.main,
    borderBottomColor: theme.palette.primary.main,
    fontWeight: 600,
  },
  tabCount: {
    fontSize: '0.65rem',
    fontWeight: 700,
    backgroundColor:
      theme.palette.type === 'dark'
        ? theme.palette.grey[700]
        : theme.palette.grey[200],
    borderRadius: 10,
    padding: '1px 5px',
    lineHeight: 1.5,
    transition: 'background-color 0.15s, color 0.15s',
  },
  tabCountActive: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
  },
  ouFilter: {
    minWidth: 180,
  },
  ouFilterSelect: {
    fontSize: '0.725rem',
    height: 28,
    padding: '0 8px',
    display: 'flex',
    alignItems: 'center',
    '& .MuiSelect-select': {
      padding: '0 24px 0 8px',
      fontSize: '0.725rem',
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      height: '100%',
      minHeight: 'unset',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.divider,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.text.secondary,
    },
  },
  loadingInline: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: '0.725rem',
  },
  tableWrapper: {
    width: '100%',
    overflow: 'auto',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    position: 'relative',
  },
  tableWrapperScrollable: {
    maxHeight: 'calc(100vh - 250px)',
  },
  table: {
    borderCollapse: 'separate' as const,
    borderSpacing: 0,
  },
  thName: {
    position: 'sticky' as const,
    top: 0,
    left: 0,
    zIndex: 5,
    width: NAME_COL_WIDTH,
    minWidth: NAME_COL_WIDTH,
    maxWidth: NAME_COL_WIDTH,
    backgroundColor: theme.palette.background.default,
    borderBottom: `2px solid ${theme.palette.divider}`,
    borderRight: `1px solid ${theme.palette.divider}`,
    padding: '5px 10px',
    verticalAlign: 'bottom' as const,
  },
  thOverall: {
    position: 'sticky' as const,
    top: 0,
    zIndex: 4,
    width: OVERALL_COL_WIDTH,
    minWidth: OVERALL_COL_WIDTH,
    backgroundColor: theme.palette.background.default,
    borderBottom: `2px solid ${theme.palette.divider}`,
    borderRight: `2px solid ${theme.palette.divider}`,
    padding: '5px 4px',
    textAlign: 'center' as const,
    cursor: 'pointer',
    userSelect: 'none' as const,
    verticalAlign: 'bottom' as const,
    '&:hover': {
      backgroundColor:
        theme.palette.type === 'dark'
          ? theme.palette.grey[700]
          : theme.palette.grey[200],
    },
  },
  thCategory: {
    position: 'sticky' as const,
    top: GROUP_HEADER_HEIGHT,
    zIndex: 3,
    minWidth: 58,
    maxWidth: 78,
    backgroundColor: theme.palette.background.default,
    borderBottom: `2px solid ${theme.palette.divider}`,
    borderRight: `1px solid ${theme.palette.divider}`,
    padding: '0px',
    verticalAlign: 'bottom' as const,
    cursor: 'pointer',
    userSelect: 'none' as const,
    '&:hover $thCategoryLabel': {
      backgroundColor:
        theme.palette.type === 'dark'
          ? theme.palette.grey[700]
          : theme.palette.grey[200],
    },
  },
  thCategoryGroupEnd: {
    borderRight: `2px solid ${theme.palette.divider}`,
  },
  thCategoryLabel: {
    flex: 1,
    padding: '2px 4px 5px 4px',
    fontSize: '0.65rem',
    fontWeight: 600,
    lineHeight: 1.2,
    whiteSpace: 'normal' as const,
    display: 'flex',
    alignItems: 'flex-end' as const,
    gap: 2,
  },
  tdName: {
    position: 'sticky' as const,
    left: 0,
    zIndex: 2,
    width: NAME_COL_WIDTH,
    minWidth: NAME_COL_WIDTH,
    maxWidth: NAME_COL_WIDTH,
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRight: `1px solid ${theme.palette.divider}`,
    padding: '2px 8px',
    overflow: 'hidden',
  },
  td: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRight: `1px solid ${theme.palette.divider}`,
    padding: '2px 3px',
    whiteSpace: 'nowrap' as const,
    textAlign: 'center' as const,
  },
  tdOverall: { borderRight: `2px solid ${theme.palette.divider}` },
  tdGroupEnd: { borderRight: `2px solid ${theme.palette.divider}` },
  ouRowBg: {
    backgroundColor:
      theme.palette.type === 'dark'
        ? theme.palette.grey[800]
        : theme.palette.grey[100],
  },
  teamRowBg: {
    backgroundColor: theme.palette.background.paper,
    '&:hover td': {
      backgroundColor:
        theme.palette.type === 'dark'
          ? theme.palette.grey[700]
          : theme.palette.grey[50],
    },
  },
  scoreWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
    justifyContent: 'center',
  },
  dot: {
    width: 5,
    height: 5,
    borderRadius: '50%',
    flexShrink: 0,
  },
  red: { backgroundColor: theme.palette.error.main },
  orange: { backgroundColor: '#ffa726' },
  yellow: { backgroundColor: '#fdd835' },
  green: { backgroundColor: '#66bb6a' },
  darkGreen: { backgroundColor: theme.palette.success.main },
  na: { backgroundColor: theme.palette.action.disabledBackground },
  loadingOverlayFull: {
    position: 'absolute' as const,
    inset: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(0,0,0,0.55)'
        : 'rgba(255,255,255,0.75)',
    zIndex: 10,
    gap: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
  },
  sortDot: {
    display: 'inline-block',
    width: 0,
    height: 0,
    flexShrink: 0,
    opacity: 0.3,
    borderLeft: '3px solid transparent',
    borderRight: '3px solid transparent',
  },
  sortDotNeutral: {},
  thGroupHeader: {
    position: 'sticky' as const,
    top: 0,
    zIndex: 3,
    backgroundColor: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRight: `1px solid ${theme.palette.divider}`,
    padding: '2px 6px',
    textAlign: 'center' as const,
    fontSize: '0.6rem',
    fontWeight: 800,
    letterSpacing: '0.07em',
    textTransform: 'uppercase' as const,
    whiteSpace: 'nowrap' as const,
    userSelect: 'none' as const,
  },
  thGroupHeaderEnd: {
    borderRight: `2px solid ${theme.palette.divider}`,
  },
  groupCritical: { color: theme.palette.error.main },
  groupHigh: { color: theme.palette.warning.main },
  groupMedium: { color: theme.palette.info.main },
  groupLow: { color: theme.palette.success.main },
  groupOther: { color: theme.palette.text.secondary },
}));

const getSortCharacter = (active: boolean, sortDir: SortDir): string => {
  if (!active) return '⇅';
  return sortDir === 'asc' ? '▲' : '▼';
};

const SortIndicator = ({
  col,
  sortKey,
  sortDir,
}: {
  col: string;
  sortKey: SortKey;
  sortDir: SortDir;
}) => {
  const active = sortKey === col && sortDir !== null;
  const color = active ? '#1a73e8' : 'rgba(0,0,0,0.25)';
  const char = getSortCharacter(active, sortDir);
  return (
    <span
      style={{
        fontSize: '0.5rem',
        color,
        lineHeight: 1,
        flexShrink: 0,
        display: 'inline-block',
        marginLeft: 1,
        verticalAlign: 'middle',
        fontStyle: 'normal',
      }}
    >
      {char}
    </span>
  );
};

const ScoreCell = ({
  score,
  classes,
}: {
  score: number | 'N/A';
  classes: ReturnType<typeof useStyles>;
}) => {
  let dotCls = classes.na;
  if (score !== 'N/A') {
    if (score < 20) dotCls = classes.red;
    else if (score < 40) dotCls = classes.orange;
    else if (score < 60) dotCls = classes.yellow;
    else if (score < 90) dotCls = classes.green;
    else dotCls = classes.darkGreen;
  }
  return (
    <span className={classes.scoreWrapper}>
      <span className={`${classes.dot} ${dotCls}`} />
      <span
        style={{
          fontWeight: score === 'N/A' ? 400 : 500,
          fontSize: '0.725rem',
        }}
      >
        {score === 'N/A' ? 'N/A' : `${score}%`}
      </span>
    </span>
  );
};

const findTeamScore = (
  value: CategoryPeriodValue,
  teamId: string,
): number | 'N/A' => {
  if (!value) return 'N/A';
  if (value.team_id === teamId) {
    return typeof value.aggregated_category_score_percentage === 'number' &&
      value.aggregated_category_score_percentage !== -1
      ? Number((value.aggregated_category_score_percentage * 100).toFixed(2))
      : 'N/A';
  }
  if (value.children?.length) {
    for (const child of value.children) {
      const found = findTeamScore(child, teamId);
      if (typeof found === 'number') return found;
    }
  }
  return 'N/A';
};

const getCurrentMonthPeriods = (categoriesData: CategoryData[]) =>
  categoriesData.map(cd => ({
    category: cd.category,
    value: cd.data?.periods?.[0]?.value || null,
  }));

function flattenOrgTree(
  orgTrees: TeamDescendantsTree[],
  lastValidPeriods: { category: string; value: CategoryPeriodValue | null }[],
): FlatRow[] {
  const rows: FlatRow[] = [];
  const computeScores = (teamId: string) => {
    const scores: Record<string, number | 'N/A'> = {};
    lastValidPeriods.forEach(({ category, value }) => {
      scores[category] = value ? findTeamScore(value, teamId) : 'N/A';
    });
    const nums = Object.values(scores).filter(
      (v): v is number => typeof v === 'number',
    );
    return {
      scores,
      overall:
        nums.length > 0
          ? Number((nums.reduce((a, b) => a + b, 0) / nums.length).toFixed(2))
          : ('N/A' as const),
    };
  };
  const walkNode = (
    node: TeamDescendantsTree,
    depth: number,
    parentId: string | null,
    parentOuName: string,
  ) => {
    const isOu = depth === 0;
    const ouName = isOu ? node.team_id : parentOuName;
    const { scores, overall } = computeScores(node.team_id);

    rows.push({
      id: `${parentId ?? ''}-${node.team_id}`,
      teamId: node.team_id,
      depth,
      isOrgUnit: isOu,
      hasChildren: node.children.length > 0,
      parentId,
      scores,
      overall,
      ouName,
    });
    node.children.forEach(child =>
      walkNode(child, depth + 1, `${parentId ?? ''}-${node.team_id}`, ouName),
    );
  };
  orgTrees.forEach(ou => walkNode(ou, 0, null, ''));
  return rows;
}

const SkeletonRows = ({
  count,
  colCount,
  classes,
}: {
  count: number;
  colCount: number;
  classes: ReturnType<typeof useStyles>;
}) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <TableRow key={i}>
        <TableCell className={classes.tdName}>
          <Skeleton variant="text" width="75%" height={14} />
        </TableCell>
        <TableCell className={classes.td}>
          <Skeleton
            variant="text"
            width={36}
            height={14}
            style={{ margin: 'auto' }}
          />
        </TableCell>
        {Array.from({ length: colCount }).map((__, j) => (
          <TableCell key={j} className={classes.td}>
            <Skeleton
              variant="text"
              width={36}
              height={14}
              style={{ margin: 'auto' }}
            />
          </TableCell>
        ))}
      </TableRow>
    ))}
  </>
);

type TreeTableProps = {
  rows: FlatRow[];
  categories: string[];
  teamsById: Map<string, string>;
  fullyExpanded?: boolean;
  loading?: boolean;
  currentMonth: string;
  tab: TabView;
  hiddenCols?: Set<string>;
};
const scoreVal = (v: number | 'N/A') => (v === 'N/A' ? -1 : v);

const getOuFilterLabel = (selected: string[]): string => {
  if (selected.length === 0) return 'All OUs';
  if (selected.length === 1) return selected[0];
  return `${selected.length} OUs`;
};

const resolveGroups = (categories: string[]): ColGroup[] => {
  const catSet = new Set(categories);
  const result: ColGroup[] = [];
  for (const group of COLUMN_GROUPS) {
    const matched = group.cols.filter(c => catSet.has(c));
    if (matched.length > 0) {
      result.push({ label: group.label, cols: matched });
      matched.forEach(c => catSet.delete(c));
    }
  }
  const ungrouped = categories.filter(c => catSet.has(c));
  if (ungrouped.length > 0) result.push({ label: 'Other', cols: ungrouped });
  return result;
};

const TreeTable = ({
  rows,
  categories,
  teamsById,
  fullyExpanded = false,
  loading = false,
  currentMonth,
  tab,
  hiddenCols,
}: TreeTableProps) => {
  const classes = useStyles();
  const buildLink = useScorecardLinkBuilder();
  const [sortKey, setSortKey] = useState<SortKey>('overall');
  const [sortDir, setSortDir] = useState<SortDir>(null);

  const handleSort = useCallback(
    (col: SortKey) => {
      if (sortKey !== col) {
        setSortKey(col);
        setSortDir('asc');
      } else {
        setSortDir(current => {
          if (current === 'asc') return 'desc';
          if (current === 'desc') return null;
          return 'asc';
        });
      }
    },
    [sortKey],
  );

  const groups = useMemo(() => resolveGroups(categories), [categories]);
  const orderedCats = useMemo(
    () => groups.flatMap(g => g.cols).filter(c => !hiddenCols?.has(c)),
    [groups, hiddenCols],
  );

  const groupEndCols = useMemo(() => {
    const ends = new Set<string>();
    groups.forEach(g => {
      const visible = g.cols.filter(c => !hiddenCols?.has(c));
      if (visible.length > 0) ends.add(visible[visible.length - 1]);
    });
    return ends;
  }, [groups, hiddenCols]);

  const buildExpanded = useCallback(
    (rowList: FlatRow[]) => {
      const set = new Set<string>();
      if (fullyExpanded) {
        rowList.forEach(r => {
          if (r.hasChildren) set.add(r.id);
        });
      }
      return set;
    },
    [fullyExpanded],
  );

  const [expanded, setExpanded] = useState<Set<string>>(() =>
    buildExpanded(rows),
  );
  useEffect(() => {
    setExpanded(buildExpanded(rows));
  }, [rows, buildExpanded]);

  const toggle = useCallback((id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const visibleRows = useMemo(() => {
    const byId = new Map<string, FlatRow>();
    rows.forEach(r => byId.set(r.id, r));

    const visible = rows.filter(row => {
      let cur = row;
      while (cur.parentId !== null) {
        const parent = byId.get(cur.parentId);
        if (!parent || !expanded.has(parent.id)) return false;
        cur = parent;
      }
      return true;
    });

    const childrenFirstCompare = (a: FlatRow, b: FlatRow) => {
      if (a.hasChildren !== b.hasChildren) return a.hasChildren ? -1 : 1;
      return 0;
    };

    if (sortDir === null) {
      const sortChildren0 = (
        parentId: string | null,
        depth: number,
      ): FlatRow[] => {
        const directChildren = visible.filter(r => r.parentId === parentId);
        const sorted =
          depth === 0
            ? directChildren
            : [...directChildren].sort(childrenFirstCompare);
        return sorted.flatMap(row => [
          row,
          ...sortChildren0(row.id, depth + 1),
        ]);
      };
      return sortChildren0(null, 0);
    }

    const compare = (a: FlatRow, b: FlatRow) => {
      const av =
        sortKey === 'overall'
          ? scoreVal(a.overall)
          : scoreVal(a.scores[sortKey] ?? 'N/A');
      const bv =
        sortKey === 'overall'
          ? scoreVal(b.overall)
          : scoreVal(b.scores[sortKey] ?? 'N/A');
      return sortDir === 'asc' ? av - bv : bv - av;
    };

    const visibleSet = new Set(visible.map(r => r.id));

    const sortChildren = (
      parentId: string | null,
      depth: number,
    ): FlatRow[] => {
      const directChildren = visible.filter(
        r => r.parentId === parentId && visibleSet.has(r.id),
      );
      const sorted =
        depth === 0 ? directChildren : [...directChildren].sort(compare);
      return sorted.flatMap(row => [row, ...sortChildren(row.id, depth + 1)]);
    };

    return sortChildren(null, 0);
  }, [rows, expanded, sortKey, sortDir]);

  const wrapperClass = [
    classes.tableWrapper,
    tab === 'all' ? classes.tableWrapperScrollable : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Paper
      elevation={0}
      style={{
        overflow: 'hidden',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: 4,
        position: 'relative',
      }}
    >
      {loading && rows.length > 0 && (
        <div className={classes.loadingOverlayFull}>
          <CircularProgress size={24} thickness={4} />
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ fontSize: '0.725rem' }}
          >
            Loading…
          </Typography>
        </div>
      )}
      <div className={wrapperClass}>
        <Table size="small" className={classes.table}>
          <TableHead>
            {/* Row 1: merged group headers */}
            <TableRow style={{ height: GROUP_HEADER_HEIGHT }}>
              <TableCell className={classes.thName} rowSpan={2}>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    lineHeight: 1.25,
                  }}
                >
                  Team / Organization Unit
                </div>
                <div
                  style={{
                    fontWeight: 400,
                    fontSize: '0.6rem',
                    opacity: 0.5,
                    marginTop: 1,
                  }}
                >
                  {currentMonth}
                </div>
              </TableCell>
              <TableCell
                className={classes.thOverall}
                rowSpan={2}
                onClick={() => handleSort('overall')}
              >
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: '0.725rem',
                    lineHeight: 1.2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                  }}
                >
                  Overall
                  <SortIndicator
                    col="overall"
                    sortKey={sortKey}
                    sortDir={sortDir}
                  />
                </div>
              </TableCell>
              {(() => {
                const lastVisibleIdx = groups.reduce(
                  (last, g, i) =>
                    g.cols.some(c => !hiddenCols?.has(c)) ? i : last,
                  -1,
                );
                return groups.map((group, gi) => {
                  const span = group.cols.filter(
                    c => !hiddenCols?.has(c),
                  ).length;
                  if (span === 0) return null;
                  const colorCls = (() => {
                    switch (group.label) {
                      case 'Critical':
                        return classes.groupCritical;
                      case 'High':
                        return classes.groupHigh;
                      case 'Medium':
                        return classes.groupMedium;
                      case 'Low':
                        return classes.groupLow;
                      default:
                        return classes.groupOther;
                    }
                  })();
                  return (
                    <TableCell
                      key={group.label}
                      colSpan={span}
                      className={`${classes.thGroupHeader} ${colorCls}${
                        gi === lastVisibleIdx
                          ? ` ${classes.thGroupHeaderEnd}`
                          : ''
                      }`}
                    >
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        <span
                          style={{
                            display: 'inline-block',
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: 'currentColor',
                            flexShrink: 0,
                          }}
                        />
                        {group.label}
                      </span>
                    </TableCell>
                  );
                });
              })()}
            </TableRow>
            {/* Row 2: individual column headers */}
            <TableRow>
              {orderedCats.map(cat => {
                const isEnd = groupEndCols.has(cat);
                return (
                  <TableCell
                    key={cat}
                    className={`${classes.thCategory}${
                      isEnd ? ` ${classes.thCategoryGroupEnd}` : ''
                    }`}
                    onClick={() => handleSort(cat)}
                  >
                    <div className={classes.thCategoryLabel}>
                      <span style={{ flex: 1 }}>{cat}</span>
                      <SortIndicator
                        col={cat}
                        sortKey={sortKey}
                        sortDir={sortDir}
                      />
                    </div>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && rows.length === 0 ? (
              <SkeletonRows
                count={10}
                colCount={orderedCats.length}
                classes={classes}
              />
            ) : (
              visibleRows.map(row => {
                const isExpanded = expanded.has(row.id);
                const rawName = row.isOrgUnit
                  ? stripManager(row.teamId)
                  : stripOuPrefix(
                      teamsById.get(row.teamId) || row.teamId,
                      row.ouName,
                    );
                if (row.isOrgUnit) {
                  return (
                    <TableRow key={row.id}>
                      <TableCell
                        className={`${classes.tdName} ${classes.ouRowBg}`}
                        style={{ padding: '2px 8px' }}
                      >
                        <Box display="flex" alignItems="center">
                          {row.hasChildren && (
                            <IconButton
                              size="small"
                              onClick={() => toggle(row.id)}
                              style={{ padding: 1, marginRight: 2 }}
                            >
                              {isExpanded ? (
                                <ExpandMoreIcon
                                  style={{ fontSize: '0.85rem' }}
                                />
                              ) : (
                                <ChevronRightIcon
                                  style={{ fontSize: '0.85rem' }}
                                />
                              )}
                            </IconButton>
                          )}
                          <FolderIcon
                            color="action"
                            style={{ marginRight: 4, fontSize: '0.8rem' }}
                          />
                          <span
                            style={{ fontWeight: 700, fontSize: '0.725rem' }}
                          >
                            {rawName}
                          </span>
                        </Box>
                      </TableCell>

                      {/* Overall score for OU */}
                      <TableCell
                        className={`${classes.td} ${classes.tdOverall} ${classes.ouRowBg}`}
                      >
                        {row.overall === 'N/A' ? (
                          <span style={{ color: '#bbb', fontSize: '0.725rem' }}>
                            N/A
                          </span>
                        ) : (
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            style={{ gap: 2 }}
                          >
                            <PercentageIcon percentage={row.overall} />
                            <span
                              style={{ fontSize: '0.725rem', fontWeight: 600 }}
                            >
                              {row.overall}%
                            </span>
                          </Box>
                        )}
                      </TableCell>

                      {orderedCats.map(cat => {
                        const isEnd = groupEndCols.has(cat);
                        return (
                          <TableCell
                            key={cat}
                            className={`${classes.td} ${classes.ouRowBg}${
                              isEnd ? ` ${classes.tdGroupEnd}` : ''
                            }`}
                          >
                            <ScoreCell
                              score={row.scores[cat] ?? 'N/A'}
                              classes={classes}
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                }

                const indentPx = (row.depth - 1) * 14;

                return (
                  <TableRow key={row.id} className={classes.teamRowBg}>
                    <TableCell
                      className={classes.tdName}
                      style={{ backgroundColor: 'inherit' }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        style={{ paddingLeft: indentPx }}
                      >
                        {row.hasChildren ? (
                          <IconButton
                            size="small"
                            onClick={() => toggle(row.id)}
                            style={{
                              padding: 1,
                              marginRight: 2,
                              flexShrink: 0,
                            }}
                          >
                            {isExpanded ? (
                              <ExpandMoreIcon style={{ fontSize: '0.85rem' }} />
                            ) : (
                              <ChevronRightIcon
                                style={{ fontSize: '0.85rem' }}
                              />
                            )}
                          </IconButton>
                        ) : (
                          <Box style={{ width: 20, flexShrink: 0 }} />
                        )}
                        <PeopleIcon
                          color="disabled"
                          style={{
                            marginRight: 4,
                            flexShrink: 0,
                            fontSize: '0.75rem',
                          }}
                        />
                        <Link
                          href={buildLink(
                            `/catalog/default/Group/${row.teamId}/scorecards`,
                          )}
                          style={{
                            textDecoration: 'none',
                            fontWeight: 600,
                            fontSize: '0.725rem',
                            color: 'inherit',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                          }}
                        >
                          {rawName}
                        </Link>
                      </Box>
                    </TableCell>

                    <TableCell className={`${classes.td} ${classes.tdOverall}`}>
                      {row.overall === 'N/A' ? (
                        <span style={{ color: '#bbb', fontSize: '0.725rem' }}>
                          N/A
                        </span>
                      ) : (
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          style={{ gap: 2 }}
                        >
                          <PercentageIcon percentage={row.overall} />
                          <span
                            style={{ fontSize: '0.725rem', fontWeight: 600 }}
                          >
                            {row.overall}%
                          </span>
                        </Box>
                      )}
                    </TableCell>

                    {orderedCats.map(cat => {
                      const isEnd = groupEndCols.has(cat);
                      return (
                        <TableCell
                          key={cat}
                          className={`${classes.td}${
                            isEnd ? ` ${classes.tdGroupEnd}` : ''
                          }`}
                        >
                          <ScoreCell
                            score={row.scores[cat] ?? 'N/A'}
                            classes={classes}
                          />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </Paper>
  );
};

export const AllScorecardsPage = () => {
  const classes = useStyles();
  const theme = useTheme<any>();
  const { teamsById, loading: tL } = useGetTeams();
  const { value: categories, loading: cL } = useGetCategories();
  const { ouName: currentUserOu, loading: ouLoading } = useCurrentUserOu();

  const [tab, setTab] = useState<TabView>('mine');
  const [ouFilters, setOuFilters] = useState<string[]>([]);
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(new Set());
  const [colFilterAnchor, setColFilterAnchor] = useState<HTMLElement | null>(
    null,
  );

  const { teamOrgTree } = useTeamOrgTree(EntityKinds.ou);
  const currentMonth = useMemo(() => DateTime.now().toFormat('MMMM yyyy'), []);
  const dates = useMemo(
    () => ({
      start: toStandardISO(DateTime.now().startOf('month')),
      end: toStandardISO(DateTime.now()),
    }),
    [],
  );

  const allOrgTrees = useMemo(
    () => (teamOrgTree?.orgTrees ?? []) as unknown as TeamDescendantsTree[],
    [teamOrgTree],
  );
  const ouNames = useMemo(
    () => allOrgTrees.map(t => t.team_id).sort((a, b) => a.localeCompare(b)),
    [allOrgTrees],
  );

  const myOrgTrees = useMemo(() => {
    if (!currentUserOu || !allOrgTrees.length) return [];
    return allOrgTrees.filter(
      t => t.team_id.toLowerCase() === currentUserOu.toLowerCase(),
    );
  }, [allOrgTrees, currentUserOu]);

  const filteredAllOrgTrees = useMemo(() => {
    if (ouFilters.length === 0) return allOrgTrees;
    return allOrgTrees.filter(t => ouFilters.includes(t.team_id));
  }, [allOrgTrees, ouFilters]);

  const activeOrgTrees =
    tab === 'mine' && myOrgTrees.length > 0 ? myOrgTrees : filteredAllOrgTrees;
  const { value: dOU, loading: l2 } = useGetCategoriesData(
    activeOrgTrees,
    dates.start,
    dates.end,
    'month',
  );
  const lastValidPeriods = useMemo(
    () => (dOU ? getCurrentMonthPeriods(dOU) : []),
    [dOU],
  );

  const categoryNames = useMemo(
    () =>
      (categories ?? [])
        .map((c: any) => c.name)
        .filter((name: string) => !EXCLUDED_CATEGORIES.has(name)),
    [categories],
  );

  const allRows = useMemo(() => {
    if (!filteredAllOrgTrees.length || !lastValidPeriods.length) return [];
    return flattenOrgTree(filteredAllOrgTrees, lastValidPeriods);
  }, [filteredAllOrgTrees, lastValidPeriods]);

  const myRows = useMemo(() => {
    if (!myOrgTrees.length || !lastValidPeriods.length) return [];
    return flattenOrgTree(myOrgTrees, lastValidPeriods);
  }, [myOrgTrees, lastValidPeriods]);

  const allRowsUnfiltered = useMemo(() => {
    if (!allOrgTrees.length || !lastValidPeriods.length) return [];
    return flattenOrgTree(allOrgTrees, lastValidPeriods);
  }, [allOrgTrees, lastValidPeriods]);

  const visibleRows =
    tab === 'mine' && myOrgTrees.length > 0 ? myRows : allRows;
  const isInitialLoading = (cL || tL || ouLoading) && !allRows.length;
  const isRefreshing = l2;

  const handleExport = useCallback(() => {
    const teamRows = visibleRows.filter(r => !r.isOrgUnit);
    const groups = resolveGroups(categoryNames);
    const orderedCats = groups.flatMap(g => g.cols);
    const exportData = teamRows.map(row => {
      const ouRow = visibleRows.find(
        r => r.isOrgUnit && row.id.startsWith(r.id),
      );
      const rawTeamName = teamsById.get(row.teamId) || row.teamId;
      const record: Record<string, string> = {
        'Organisation Unit': ouRow?.teamId ?? '',
        Team: stripManager(stripOuPrefix(rawTeamName, row.ouName)),
        Overall: row.overall === 'N/A' ? 'N/A' : `${row.overall}%`,
      };
      orderedCats.forEach(cat => {
        const score = row.scores[cat];
        record[cat] =
          score === 'N/A' || score === undefined ? 'N/A' : `${score}%`;
      });
      return record;
    });
    const ws = XLSX.utils.json_to_sheet(exportData);
    const colWidths = Object.keys(exportData[0] ?? {}).map(key => ({
      wch: Math.max(
        key.length + 2,
        ...exportData.map(r => String(r[key] ?? '').length),
      ),
    }));
    ws['!cols'] = colWidths;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `Scorecards ${currentMonth}`);
    XLSX.writeFile(
      wb,
      `all-scorecards-${currentMonth.toLowerCase().replace(' ', '-')}.xlsx`,
    );
  }, [visibleRows, teamsById, categoryNames, currentMonth]);

  if (isInitialLoading) {
    return (
      <Page themeId="tool">
        <Header title="All Scorecards" />
        <Content>
          <Progress />
        </Content>
      </Page>
    );
  }

  const countTeams = (rows: FlatRow[]) =>
    rows.filter(r => !r.isOrgUnit && r.depth === 1).length;

  return (
    <Page themeId="tool">
      <Header
        title="All Scorecards"
        subtitle={`Aggregated scorecards across the organisation, grouped by Organisation Unit — ${currentMonth}`}
      />
      <Content>
        <Box className={classes.toolbar}>
          <Box display="flex" alignItems="center" style={{ gap: 6 }}>
            <div className={classes.tabsContainer}>
              {(['mine', 'all'] as TabView[]).map(t => {
                const isActive = tab === t;
                const count =
                  t === 'mine'
                    ? countTeams(myRows)
                    : countTeams(allRowsUnfiltered);
                return (
                  <button
                    key={t}
                    className={`${classes.tabBtn}${
                      isActive ? ` ${classes.tabBtnActive}` : ''
                    }`}
                    onClick={() => {
                      setTab(t);
                      setOuFilters([]);
                    }}
                  >
                    {t === 'mine' ? 'Mine' : 'All'}
                    <span
                      className={`${classes.tabCount}${
                        isActive ? ` ${classes.tabCountActive}` : ''
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            {isRefreshing && visibleRows.length > 0 && (
              <Box className={classes.loadingInline}>
                <CircularProgress size={11} thickness={5} />
                <span>Loading…</span>
              </Box>
            )}
          </Box>

          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            {/* Column visibility filter */}
            <Button
              size="small"
              variant="outlined"
              startIcon={<ViewColumnIcon style={{ fontSize: 13 }} />}
              onClick={e => setColFilterAnchor(e.currentTarget)}
              style={{
                fontSize: '0.725rem',
                padding: '2px 8px',
                minHeight: 28,
                ...(hiddenCols.size > 0
                  ? {
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                    }
                  : {
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.secondary,
                    }),
              }}
            >
              Columns{hiddenCols.size > 0 ? ` (−${hiddenCols.size})` : ''}
            </Button>
            <Popover
              open={Boolean(colFilterAnchor)}
              anchorEl={colFilterAnchor}
              onClose={() => setColFilterAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Box
                style={{
                  padding: '8px 12px',
                  minWidth: 230,
                  maxHeight: 420,
                  overflowY: 'auto',
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  style={{ marginBottom: 6 }}
                >
                  <Typography style={{ fontSize: '0.75rem', fontWeight: 700 }}>
                    Visible Columns
                  </Typography>
                  {hiddenCols.size > 0 && (
                    <Button
                      size="small"
                      onClick={() => setHiddenCols(new Set())}
                      style={{
                        fontSize: '0.65rem',
                        padding: '1px 6px',
                        minHeight: 'unset',
                      }}
                    >
                      Show all
                    </Button>
                  )}
                </Box>
                {resolveGroups(categoryNames).map(group => (
                  <Box key={group.label} style={{ marginBottom: 6 }}>
                    <Typography
                      style={{
                        fontSize: '0.6rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: '#888',
                        padding: '2px 0',
                      }}
                    >
                      {group.label}
                    </Typography>
                    {group.cols.map(col => (
                      <Box
                        key={col}
                        display="flex"
                        alignItems="center"
                        style={{ cursor: 'pointer', marginLeft: -4 }}
                        onClick={() =>
                          setHiddenCols(prev => {
                            const next = new Set(prev);
                            if (next.has(col)) next.delete(col);
                            else next.add(col);
                            return next;
                          })
                        }
                      >
                        <Checkbox
                          size="small"
                          checked={!hiddenCols.has(col)}
                          style={{ padding: 3 }}
                        />
                        <Typography style={{ fontSize: '0.725rem' }}>
                          {col}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </Popover>

            {/* OU filter (multi-select) */}
            {tab === 'all' && (
              <FormControl
                variant="outlined"
                size="small"
                className={classes.ouFilter}
              >
                <Select
                  multiple
                  value={ouFilters}
                  onChange={e => setOuFilters(e.target.value as string[])}
                  displayEmpty
                  className={classes.ouFilterSelect}
                  renderValue={selected => {
                    const arr = selected as string[];
                    return (
                      <Box
                        display="flex"
                        alignItems="center"
                        style={{ gap: 4 }}
                      >
                        <FilterListIcon
                          style={{ fontSize: 12, opacity: 0.55 }}
                        />
                        <span
                          style={{
                            fontSize: '0.725rem',
                            opacity: arr.length === 0 ? 0.65 : 1,
                          }}
                        >
                          {getOuFilterLabel(arr)}
                        </span>
                      </Box>
                    );
                  }}
                >
                  {ouNames.map(ou => (
                    <MenuItem key={ou} value={ou}>
                      <Checkbox
                        size="small"
                        checked={ouFilters.includes(ou)}
                        style={{ padding: 2, marginRight: 4 }}
                      />
                      <ListItemText
                        primary={ou}
                        primaryTypographyProps={{
                          style: { fontSize: '0.725rem' },
                        }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon style={{ fontSize: 13 }} />}
              onClick={handleExport}
              disabled={visibleRows.filter(r => !r.isOrgUnit).length === 0}
              style={{
                fontSize: '0.725rem',
                padding: '2px 8px',
                minHeight: 28,
              }}
            >
              Export ({visibleRows.filter(r => !r.isOrgUnit).length})
            </Button>
          </Box>
        </Box>

        <TreeTable
          rows={visibleRows}
          categories={categoryNames}
          teamsById={teamsById}
          fullyExpanded={tab === 'mine'}
          loading={isRefreshing && visibleRows.length === 0}
          currentMonth={currentMonth}
          tab={tab}
          hiddenCols={hiddenCols}
        />
      </Content>
    </Page>
  );
};
